import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import DefaultLoader from "../jsx/components/Loader";
import Swal from "sweetalert2";
import AlertResponses from "../jsx/components/Alert";
import ProgressBarComponent from "../jsx/components/ProgressBar";
import UserContext from "../contexts/UserContext";
import styled from "styled-components";
import { bulkDelete, getTemplates } from "../services/TemplatesServices";
import { normalizeTemplatesData } from "../helpers/TemplatesHelpers";
import { IconPlus } from "@tabler/icons-react";
import NotificationsContext from "../contexts/NotificationsContext";

// Styled Components
const Container = styled.div`
  margin: 0;
  background: var(--background);

  @media (max-width: 768px) {
    background: var(--background);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const BackButton = styled.button`
  background: var(--surface);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Title = styled.h2`
  color: var(--text-primary);
  font-weight: 700;
  font-size: 28px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ActionButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    padding: 12px 20px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  font-size: 16px;
  background: white;
  transition: all 0.3s ease;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-100);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  font-size: 18px;
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SelectionInfo = styled.div`
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 14px;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const DeleteButton = styled.button`
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--danger-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 100px;
  }
`;

const TemplateCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--shadow-sm);
  border: 2px solid
    ${(props) => (props.selected ? "var(--primary)" : "transparent")};
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    padding: 16px;

    &:hover {
      transform: none;
    }

    &:active {
      background: var(--gray-50);
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  margin-right: 8px;
  line-height: 1.3;
  flex-shrink: 1;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CardCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--primary);
`;

const CardContent = styled.div`
  margin-bottom: 16px;
`;

const MessagePreview = styled.p`
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeBadge = styled.span`
  display: inline-block;
  background: ${(props) => {
    switch (props.type) {
      case "Áudio":
        return "var(--success)";
      case "Documento":
        return "var(--gray-500)";
      case "Imagem":
        return "var(--info)";
      case "Email":
        return "var(--primary)";
      default:
        return "var(--primary)";
    }
  }};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

const TemplateBadge = styled.span`
  display: inline-block;
  background: ${(props) => {
    switch (props.templateType) {
      case "whatsapp":
        return "#25D366";
      case "email":
        return "#4285F4";
      case "webhook":
        return "#FF6B6B";
      default:
        return "#25D366";
    }
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 8px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
`;

const UpdateDate = styled.span`
  color: var(--text-muted);
  font-size: 12px;
`;

const EditButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: var(--primary-hover);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  box-shadow: var(--shadow-lg);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--primary-hover);
    transform: scale(1.05);
    box-shadow: var(--shadow-xl);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    bottom: 70px;
    right: 16px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 32px 0;

  @media (max-width: 768px) {
    margin: 24px 0;
    flex-wrap: wrap;
  }
`;

const PaginationButton = styled.button`
  background: ${(props) => (props.active ? "var(--primary)" : "white")};
  color: ${(props) => (props.active ? "white" : "var(--text-secondary)")};
  border: 2px solid
    ${(props) => (props.active ? "var(--primary)" : "var(--border-color)")};
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.active ? "var(--primary-hover)" : "var(--surface-hover)"};
    border-color: ${(props) =>
      props.active ? "var(--primary-hover)" : "var(--border-color-hover)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-muted);

  h3 {
    color: var(--text-secondary);
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 24px;
  }
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const ViewModeToggle = styled.div`
  display: flex;
  background: var(--surface);
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 20px;
`;

const ViewButton = styled.button`
  background: ${(props) => (props.active ? "var(--primary)" : "transparent")};
  color: ${(props) => (props.active ? "white" : "var(--text-secondary)")};
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--primary-hover)" : "var(--surface-hover)"};
  }
`;

export default function Templates({ agentId = null, hubMode = false, agentContext = null }) {
  const { user } = useContext(UserContext);
  const {setNotify} = useContext(NotificationsContext)
  const navigate = useNavigate();
  
  // Detecta se está dentro do AgentHub (similar ao AutomationsList)
  const isInsideAgentHub = Boolean(agentContext) || window.location.pathname.includes('/agent-hub/') || hubMode;
  const [templatesData, setTemplatesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
 
  const [progress, setProgress] = useState(false);

  useEffect(() => {
    getTemplates('', agentId)
      .then((data) => {
        const normalizedData = normalizeTemplatesData(data.results || data);
        setTemplatesData(normalizedData);
        setFilteredData(normalizedData);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [agentId]);

  // Filter data based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(templatesData);
    } else {
      const filtered = templatesData.filter((template) =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, templatesData]);

  // Update delete button visibility
  useEffect(() => {
    setShowDeleteButton(selectedRows.length > 0);
  }, [selectedRows]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleCheckboxChange = (templateId, isChecked) => {
    if (isChecked) {
      setSelectedRows((prev) => [...prev, templateId]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== templateId));
    }
  };


  const handleDelete = () => {
    Swal.fire({
      title: "Você tem certeza?",
      text: "Todos os templates selecionados serão deletados. Esta ação é irreversível!",
      icon: "warning",
      buttons: true,
      confirmButtonText: `Continuar a ação`,
      cancelButtonText: `Cancelar`,
      showCancelButton: true,
      confirmButtonColor: "red",
    }).then((value) => {
      if (value.isConfirmed) {
        let payload = {
          ids: selectedRows,
          account_id: user.account_id,
        };

        // Determina a URL de redirecionamento baseada no contexto
        const redirectUrl = isInsideAgentHub && agentId 
          ? `/agent-hub/${agentId}/templates` 
          : "/templates";
          
        bulkDelete(payload, setNotify, setProgress, redirectUrl).then((res) => {
          if (res) {
            setSelectedRows([]);
            setShowDeleteButton(false);
            // Refresh data
            getTemplates('', agentId).then((data) => {
              const normalizedData = normalizeTemplatesData(
                data.results || data
              );
              setTemplatesData(normalizedData);
              setFilteredData(normalizedData);
            });
          }
        });
      }
    });
  };

  const getMessageType = (message, templateType) => {
    if (templateType === "email") {
      return "Email";
    }

    try {
      let type = JSON.parse(message);
      type = Object.keys(type)[0];

      switch (type) {
        case "audio":
          return "Áudio";
        case "document":
          return "Documento";
        case "image":
          return "Imagem";
        default:
          return "Texto";
      }
    } catch (error) {
      return "Texto";
    }
  };

  const getMessageText = (message, template) => {
    if (template.template_type === "email") {
      return template.email_subject || "Sem assunto definido";
    }

    try {
      return message.text || "Sem prévia disponível";
    } catch (error) {
      return "Sem prévia disponível";
    }
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString("pt-BR") : "";
  };

  const handleEditTemplate = (template) => {
    const templateType = template.template_type || "whatsapp";
    if (isInsideAgentHub && agentId) {
      navigate(`/agent-hub/${agentId}/templates/${templateType}/edit/${template.id}`);
    } else {
      navigate(`/templates/${templateType}/edit/${template.id}`);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <PaginationButton
        key="prev"
        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        disabled={currentPage === 1}
      >
        ←
      </PaginationButton>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationButton
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </PaginationButton>
      );
    }

    // Next button
    pages.push(
      <PaginationButton
        key="next"
        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        disabled={currentPage === totalPages}
      >
        →
      </PaginationButton>
    );

    return <PaginationContainer>{pages}</PaginationContainer>;
  };

  if (isLoading) {
    return <DefaultLoader isLoading={isLoading} />;
  }


  return (
    <div className={isInsideAgentHub ? "p-4" : "px-4 py-2"}>
      <Header>
        <HeaderLeft>
         
          <Title>Templates</Title>
        </HeaderLeft>

        <HeaderActions>
          <ActionButton onClick={() => {
            if (isInsideAgentHub && agentId) {
              navigate(`/agent-hub/${agentId}/templates/new`);
            } else {
              navigate("/templates/new");
            }
          }}>
            <IconPlus size={18} />
            Novo Template
          </ActionButton>
        </HeaderActions>
      </Header>

      <>
        <SearchContainer>
          <SearchIcon>🔍</SearchIcon>
          <SearchInput
            type="text"
            placeholder="Buscar templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        {(selectedRows.length > 0 || filteredData.length > 0) && (
          <ActionsBar>
            <SelectionInfo>
              {selectedRows.length > 0
                ? `${selectedRows.length} template${
                    selectedRows.length > 1 ? "s" : ""
                  } selecionado${selectedRows.length > 1 ? "s" : ""}`
                : `${filteredData.length} template${
                    filteredData.length > 1 ? "s" : ""
                  } encontrado${filteredData.length > 1 ? "s" : ""}`}
            </SelectionInfo>

            {showDeleteButton && (
              <DeleteButton onClick={handleDelete}>
                Deletar Selecionados
              </DeleteButton>
            )}
          </ActionsBar>
        )}

        {currentData.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📄</EmptyIcon>
            <h3>Nenhum template encontrado</h3>
            <p>
              {searchTerm
                ? "Tente ajustar sua busca ou criar um novo template."
                : "Comece criando seu primeiro template de mensagem."}
            </p>
            <Button variant="primary" onClick={() => {
              if (isInsideAgentHub && agentId) {
                navigate(`/agent-hub/${agentId}/templates/new`);
              } else {
                navigate("/templates/new");
              }
            }}>
              Criar Primeiro Template
            </Button>
          </EmptyState>
        ) : (
          <>
            <CardsContainer>
              {currentData.map((template) => (
                <TemplateCard
                  key={template.id}
                  selected={selectedRows.includes(template.id)}
                  onClick={(e) => {
                    if (
                      e.target.type !== "checkbox" &&
                      !e.target.closest("button")
                    ) {
                      handleEditTemplate(template);
                    }
                  }}
                >
                  <CardHeader>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <CardTitle>{template.name}</CardTitle>
                      <TemplateBadge templateType={template.template_type || 'whatsapp'}>
                        {template.template_type || 'whatsapp'}
                      </TemplateBadge>
                    </div>
                    <CardCheckbox
                      type="checkbox"
                      checked={selectedRows.includes(template.id)}
                      onChange={(e) =>
                        handleCheckboxChange(template.id, e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </CardHeader>

                  <CardContent>
                    <MessagePreview>
                      {getMessageText(template.message, template)}
                    </MessagePreview>
                    <TypeBadge
                      type={getMessageType(
                        template.message,
                        template.template_type
                      )}
                    >
                      {getMessageType(template.message, template.template_type)}
                    </TypeBadge>
                  </CardContent>

                  <CardFooter>
                    <UpdateDate>
                      {formatDate(template.updated_at || template.created_at)}
                    </UpdateDate>

                    {!template.ai_plugins && (
                      <EditButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTemplate(template);
                        }}
                      >
                        ✏️ Editar
                      </EditButton>
                    )}
                  </CardFooter>
                </TemplateCard>
              ))}
            </CardsContainer>

            {renderPagination()}
          </>
        )}
      </>

      {/* {notify.visible && (
        <AlertResponses
          title={notify.title}
          message={notify.message}
          type={notify.type}
          visible={notify.visible}
          setNotify={setNotify}
        />
      )} */}

      {progress && <ProgressBarComponent />}
    </div>
  );
}
